/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { memo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import Icon from 'src/components/ui/Icon/Icon';
import { useTranslation } from 'react-i18next';
import Button, { ButtonProps, ButtonToneProps } from '../../ui/Button/Button';
import styles from './Header.module.scss';
import { menuItems } from '../Nav/routes';
import { useUserAccessRightsQuery } from '../../../services/user';

interface HeaderProps {
  title?: string;
  buttons?: ButtonProps[];
  isEditMode?: boolean;
  editAction?: () => void;
  hint?: string;
}

const Header: React.FC<HeaderProps> = ({ title, buttons, isEditMode, editAction, hint }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [routes, setRoutes] = React.useState<
    {
      label: string;
      link: string;
    }[]
  >([]);

  const { data: accessRights, isLoading: isAccessLoading } = useUserAccessRightsQuery();

  const handleMenuTrigger = () => {
    document.body.classList.toggle('menu-active');
  };

  useEffect(() => {
    const { pathname } = location;
    const tmpRoute = [];

    const parentRoute = menuItems
      .filter(item => item.link && (pathname === `/${item.link}` || pathname.startsWith(`/${item.link}/`)))
      .sort((a, b) => b.link.length - a.link.length)[0];
    const childEntries = parentRoute?.childs;
    if (childEntries) {
      const firstAvailableChild = childEntries.find(child =>
        accessRights?.some(right => right.route === `/${child.link}` && (right.read || right.read_write)),
      );
      if (firstAvailableChild) {
        tmpRoute.push(firstAvailableChild);
      }
    } else if (!childEntries && parentRoute) {
      tmpRoute.push({ label: parentRoute.label, link: parentRoute.link });
    }
    setRoutes(tmpRoute);
  }, [location, accessRights, menuItems]);

  return (
    <div className={classNames(styles.root)}>
      <div className="flex items-center gap-11">
        <button
          className={styles.icon}
          type="button"
          onClick={() => navigate('/dashboard')}
          aria-label="Navigate to login">
          <Icon name="logo-square-dark" width={32} height={32} />
        </button>
        {title ? (
          <div className="flex justify-center gap-x-2 typo-text-16--bold text-full">
            <div className="flex gap-4">
              <span className="typo-text-16--bold text-full text-nowrap">{title}</span>
              {isEditMode && (
                <div onClick={editAction} className="cursor-pointer" role="none">
                  <Icon className="text-full opacity-60 flex justify-center w-full" name="edit" />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex justify-center gap-x-4 typo-text-16--bold">
            {routes.map((route, index) => (
              <div key={`route-${route.label}`} className="flex items-center gap-4 text-faded last:text-full">
                <div className="flex">
                  {!route.label.includes('workflow') && (
                    <Link to={`/${route.link}`} className="typo-text-16--bold">
                      {t(route.label)}
                    </Link>
                  )}
                </div>
                {index < routes.length - 1 && <span>/</span>}
              </div>
            ))}
          </div>
        )}
        <button
          aria-label="open or close menu"
          type="button"
          className="md:hidden ml-auto pl-20 h-full"
          onClick={handleMenuTrigger}>
          <Icon name="menu" className="opacity-60" width={24} height={24} />
        </button>
      </div>
      {!!buttons && buttons.length > 0 && (
        <div className="flex items-center md:justify-between gap-x-8">
          {hint && <span className="typo-text-12 text-faded hidden md:inline mr-8">{hint}</span>}
          {(buttons || []).map(button => (
            <div key={`button-${button.title}`} className="">
              <Button
                iconPosition={button.iconPosition}
                title={button.title}
                icon={button.icon}
                tone={button.tone}
                onClick={button.onClick}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(Header);
